import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Image, Users, School, Package, ArrowRight, Camera, Link as LinkIcon, Printer, BarChart3, KeyRound, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { logError } from '@/lib/logger';

import { API } from '@/lib/apiBase';

export default function Dashboard() {
  const { user, getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState({ classes: 0, students: 0, photos: 0, orders: 0 });
  const [recentPhotos, setRecentPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teacherCode, setTeacherCode] = useState('');
  const [redeemingCode, setRedeemingCode] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { ...getAuthHeaders() };
        // Two calls: cheap /dashboard-stats for the 4 stat-card numbers (uses
        // count_documents — not capped like GET /api/photos), plus a small
        // /api/photos fetch just for the 4 recent-photo thumbnails.
        const [statsRes, photosRes] = await Promise.all([
          fetch(`${API}/dashboard-stats`, { headers, credentials: 'include' }),
          fetch(`${API}/photos`, { headers, credentials: 'include' }),
        ]);

        if (statsRes.ok) {
          const s = await statsRes.json();
          setStats({
            classes: s.classes || 0,
            students: s.students || 0,
            photos: s.photos || 0,
            orders: s.orders || 0,
          });
        }

        const photos = photosRes.ok ? await photosRes.json() : [];
        setRecentPhotos(photos.slice(0, 4));
      } catch (error) {
        logError('Dashboard.Errorfetchingdashboarddata', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const isAdmin = user?.role === 'admin';
  const isTeacher = user?.role === 'teacher';
  const isParent = user?.role === 'parent';

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="dashboard">
      <div className="mb-12">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">
          {isAdmin ? t('dashboard.adminTitle') : isTeacher ? t('dashboard.teacherTitle') : t('dashboard.parentTitle')}
        </h1>
        <p className="text-muted-foreground">
          {isAdmin ? t('dashboard.adminDesc') : isTeacher ? t('dashboard.teacherDesc') : t('dashboard.parentDesc')}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <School className="w-5 h-5 text-blue-500 mb-2" />
            <div className="text-3xl font-black mb-1">{loading ? '-' : stats.classes}</div>
            <div className="text-sm text-muted-foreground">{t('dashboard.classes')}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <Users className="w-5 h-5 text-green-500 mb-2" />
            <div className="text-3xl font-black mb-1">{loading ? '-' : stats.students}</div>
            <div className="text-sm text-muted-foreground">{t('dashboard.students')}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <Image className="w-5 h-5 text-purple-500 mb-2" />
            <div className="text-3xl font-black mb-1">{loading ? '-' : stats.photos}</div>
            <div className="text-sm text-muted-foreground">{t('dashboard.photos')}</div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-6">
            <Package className="w-5 h-5 text-primary mb-2" />
            <div className="text-3xl font-black mb-1">{loading ? '-' : stats.orders}</div>
            <div className="text-sm text-muted-foreground">{t('dashboard.orders')}</div>
          </CardContent>
        </Card>
      </div>

      {/* Teacher Code Entry - visible to parents and teachers */}
      {!isAdmin && (
        <Card className="bg-card border-border mb-12">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-bold">{t('dashboard.teacherCodeTitle')}</h3>
                <p className="text-xs text-muted-foreground">{t('dashboard.teacherCodeDesc')}</p>
              </div>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!teacherCode.trim()) return;
              setRedeemingCode(true);
              try {
                const res = await fetch(`${API}/auth/redeem-teacher-code`, {
                  method: 'POST',
                  headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ code: teacherCode.trim() })
                });
                const data = await res.json();
                if (res.ok) {
                  toast.success(`${t('dashboard.teacherCodeSuccess')} ${data.class_name}`);
                  setTeacherCode('');
                  window.location.reload();
                } else {
                  toast.error(data.detail || t('dashboard.teacherCodeInvalid'));
                }
              } catch (err) {
                toast.error(t('common.error'));
              } finally {
                setRedeemingCode(false);
              }
            }} className="flex gap-2" data-testid="teacher-code-form">
              <Input
                value={teacherCode}
                onChange={(e) => setTeacherCode(e.target.value.toUpperCase())}
                placeholder="TCXXXXXX"
                className="bg-secondary border-border font-mono uppercase flex-1 max-w-[200px]"
                data-testid="teacher-code-input"
              />
              <Button type="submit" disabled={redeemingCode || !teacherCode.trim()} className="bg-primary hover:bg-primary/90" data-testid="redeem-teacher-code-btn">
                {redeemingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : t('dashboard.redeemCode')}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="mb-12">
        <h2 className="text-xl font-bold mb-4">{t('dashboard.quickActions')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {isAdmin && (
            <>
              <Link to="/admin/classes">
                <Card className="bg-card border-border card-hover cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                      <School className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1"><div className="font-semibold">{t('dashboard.manageClasses')}</div></div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              </Link>
              <Link to="/admin/students">
                <Card className="bg-card border-border card-hover cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                      <Users className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1"><div className="font-semibold">{t('dashboard.manageStudents')}</div></div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              </Link>
              <Link to="/admin/photos">
                <Card className="bg-card border-border card-hover cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                      <Camera className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1"><div className="font-semibold">{t('dashboard.uploadPhotos')}</div></div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              </Link>
              <Link to="/admin/print-formats">
                <Card className="bg-card border-border card-hover cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                      <Printer className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1"><div className="font-semibold">{t('nav.printFormats')}</div></div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              </Link>
              <Link to="/admin/bundles">
                <Card className="bg-card border-border card-hover cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/10 rounded-sm flex items-center justify-center">
                      <Package className="w-6 h-6 text-green-500" />
                    </div>
                    <div className="flex-1"><div className="font-semibold">{t('nav.bundles')}</div></div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              </Link>
              <Link to="/admin/analytics">
                <Card className="bg-card border-border card-hover cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-blue-500" />
                    </div>
                    <div className="flex-1"><div className="font-semibold">{t('nav.analytics')}</div></div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              </Link>
            </>
          )}
          {(isTeacher || isParent) && (
            <>
              <Link to="/gallery">
                <Card className="bg-card border-border card-hover cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                      <Image className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1"><div className="font-semibold">{t('dashboard.viewGallery')}</div></div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              </Link>
              <Link to="/orders">
                <Card className="bg-card border-border card-hover cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                      <Package className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1"><div className="font-semibold">{t('dashboard.myOrders')}</div></div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </CardContent>
                </Card>
              </Link>
            </>
          )}
          {isParent && (
            <Link to="/link-student">
              <Card className="bg-card border-border card-hover cursor-pointer h-full">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
                    <LinkIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1"><div className="font-semibold">{t('dashboard.linkChild')}</div></div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </CardContent>
              </Card>
            </Link>
          )}
        </div>
      </div>

      {recentPhotos.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">{t('dashboard.recentPhotos')}</h2>
            <Button asChild variant="ghost" className="text-sm">
              <Link to="/gallery">{t('dashboard.viewAll')}<ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {recentPhotos.map((photo) => (
              <Link key={photo.photo_id} to="/gallery">
                <Card className="bg-card border-border overflow-hidden card-hover">
                  <div className="aspect-square relative">
                    <img
                      src={`${API}/photos/${photo.photo_id}/preview?size=thumb`}
                      alt="Photo preview"
                      className="w-full h-full object-cover"
                    />
                    {photo.is_class_photo && (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-primary text-xs font-bold rounded-sm">
                        {t('gallery.classPhoto').toUpperCase()}
                      </div>
                    )}
                  </div>
                  <CardContent className="p-3">
                    <div className="font-mono text-sm text-primary">CHF {photo.price?.toFixed(2)}</div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {isParent && stats.photos === 0 && !loading && (
        <Card className="bg-card border-border border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <LinkIcon className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t('dashboard.linkYourChild')}</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t('dashboard.linkChildDesc')}</p>
            <Button asChild className="bg-primary hover:bg-primary/90">
              <Link to="/link-student"><LinkIcon className="w-4 h-4 mr-2" />{t('dashboard.linkChild')}</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
